import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { WidgetEdit } from '../../../../shared/components/widget-edit/widget-edit';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { FiDataService } from '../../../../core/services/fi-data.service';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-cash-flow-preview-widget',
    standalone: true,
    imports: [CommonModule, WidgetEdit, CdkOverlayOrigin],
    templateUrl: './cash-flow-preview-widget.html',
    styleUrl: './cash-flow-preview-widget.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CashFlowPreviewWidgetComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() isEditMode: boolean = false;
    @Input() isAddMode: boolean = false;
    @Output() addWidget = new EventEmitter<void>();
    @Input() size: 'small' | 'medium' | 'half' | 'full' = 'small';
    @Output() editCall = new EventEmitter<any>();
    @Input() index = 0;
    @Input() activeDropdownIndex: number | null = null;
    @Input() getAvailableSizes!: (index: number) => Array<{ value: string; label: string }>;
    @Input() toggleSizeDropdown!: (index: number, event: Event) => void;
    @Input() changeWidgetSize!: (index: number, size: any) => void;
    @Input() removeWidget!: (index: number) => void;
    @Input() title = 'Cash Flow Preview';

    // Data inputs
    @Input() incomingAmount: number = 0;
    @Input() outgoingAmount: number = 0;
    @Input() aiAdvice: string = "You're Spending too much at 7-Eleven";
    @Input() aiName: string = "CHARLES";

    private resizeObserver: ResizeObserver | null = null;
    private subscriptions = new Subscription();

    constructor(
        private elementRef: ElementRef,
        private cdr: ChangeDetectorRef,
        private fiDataService: FiDataService
    ) { }

    ngOnInit() {
        // Subscribe to cash flow data from the service
        this.subscriptions.add(
            this.fiDataService.cashFlow$.subscribe(data => {
                this.incomingAmount = data.incoming;
                this.outgoingAmount = data.outgoing;
                this.cdr.detectChanges();
            })
        );

        // Ensure data is loaded if it hasn't been yet
        // The TotalBalance widget usually calls this, but we'll be safe
        let consentID: any = localStorage.getItem('active_consent_id');;
        if (!this.fiDataService.getFiState()) {
            this.fiDataService.getAllFIData(consentID, 'cache').subscribe();
        }
    }

    ngAfterViewInit() {
        this.setupResizeObserver();
    }

    private setupResizeObserver() {
        const widgetElement = this.elementRef.nativeElement.closest('article.widget') as HTMLElement;

        if (!widgetElement) {
            if (!this.isAddMode) {
                console.warn('Could not find parent widget element');
            }
            return;
        }

        // Initial update
        setTimeout(() => {
            this.updateDynamicStyles(widgetElement);
        }, 0);

        this.resizeObserver = new ResizeObserver(() => {
            window.requestAnimationFrame(() => {
                if (!this.resizeObserver) return;
                const isDragging = document.body.classList.contains('is-resizing');
                const resizingWidgetType = document.body.dataset['resizingWidget'];

                // Update when THIS widget is being resized OR when not dragging (size dropdown change)
                if ((isDragging && resizingWidgetType === 'cashflowpreview') || !isDragging) {
                    this.updateDynamicStyles(widgetElement);
                }
            });
        });

        this.resizeObserver.observe(widgetElement);
    }

    private updateDynamicStyles(widgetElement: HTMLElement) {
        const widgetHeight = widgetElement.offsetHeight;
        const containerElement = this.elementRef.nativeElement as HTMLElement;

        // Check widget size
        const isSmall = this.size === 'small';
        const isMedium = this.size === 'medium';
        const isHalf = this.size === 'half';
        const isFull = this.size === 'full';

        // Base values at minimum height (226px) and max height
        const minHeight = 226;
        const maxHeight = 500;

        // Calculate height ratio (0 to 1)
        const heightRatio = Math.min(Math.max((widgetHeight - minHeight) / (maxHeight - minHeight), 0), 1);

        // Scale factor: medium/small uses 1.0-1.6, half/full use 1.0-2.0 for larger max values
        const maxScale = (isMedium || isSmall) ? 1.6 : 2.0;
        const scaleFactor = 1 + (heightRatio * (maxScale - 1));

        // For medium size at minimum height, use smaller base values for AI section
        const mediumScaleFactor = isMedium && widgetHeight <= minHeight + 20 ? 0.85 : scaleFactor;

        // For small size at minimum height, use smaller base values for Cards (stacked)
        const smallScaleFactor = isSmall && widgetHeight <= minHeight + 20 ? 0.85 : scaleFactor;

        // Update flow cards
        const flowCards = containerElement.querySelectorAll('.cash-flow-widget__card') as NodeListOf<HTMLElement>;
        // Use smallScaleFactor for cards if small, otherwise scaleFactor
        const cardScaleFactor = isSmall ? smallScaleFactor : scaleFactor;

        flowCards.forEach(card => {
            // Use larger base padding for small size to achieve 12px at min height (14 * 0.85 ~= 12)
            const basePadding = isSmall ? 14 : 12;
            const padding = Math.round(basePadding * cardScaleFactor);
            card.style.padding = `${padding}px`;
            card.style.gap = `${Math.round(6 * cardScaleFactor)}px`;

            const label = card.querySelector('.cash-flow-widget__card-label') as HTMLElement;
            if (label) {
                const fontSize = Math.round(14 * cardScaleFactor);
                label.style.fontSize = `${fontSize}px`;
                label.style.lineHeight = `${Math.round(fontSize * 1.3)}px`;
            }

            const amount = card.querySelector('.cash-flow-widget__amount') as HTMLElement;
            if (amount) {
                const fontSize = Math.round(24 * cardScaleFactor);
                amount.style.fontSize = `${fontSize}px`;
                amount.style.lineHeight = `${Math.round(fontSize * 1.4)}px`;
            }

            const arrow = card.querySelector('.cash-flow-widget__arrow') as HTMLElement;
            if (arrow) {
                const arrowSize = Math.round(16 * cardScaleFactor);
                arrow.style.width = `${arrowSize}px`;
                arrow.style.height = `${arrowSize}px`;
            }
        });

        // Update AI section elements
        const aiSection = containerElement.querySelector('.cash-flow-widget__ai-section') as HTMLElement;
        if (aiSection) {
            // For half/full, use same base values as medium but allow more growth
            // At min height: scaleFactor = 1.0, at max height: scaleFactor = 1.6
            // Use same base as medium to fit at min height, scale grows with height
            const aiScaleFactor = isMedium ? mediumScaleFactor : scaleFactor;

            // AI section padding
            const aiPadding = Math.round(10 * aiScaleFactor);
            aiSection.style.padding = `${aiPadding}px`;
            aiSection.style.gap = `${Math.round(6 * aiScaleFactor)}px`;

            // AI icon - same base for all, grows with height
            const aiIcon = aiSection.querySelector('.cash-flow-widget__ai-icon') as HTMLElement;
            if (aiIcon) {
                const baseIconSize = 32;
                const iconSize = Math.round(baseIconSize * aiScaleFactor);
                aiIcon.style.width = `${iconSize}px`;
                aiIcon.style.height = `${iconSize}px`;
            }

            // AI name - same base for all, grows with height
            const aiName = aiSection.querySelector('.cash-flow-widget__ai-name') as HTMLElement;
            if (aiName) {
                const baseFontSize = 14;
                const fontSize = Math.round(baseFontSize * aiScaleFactor);
                aiName.style.fontSize = `${fontSize}px`;
                aiName.style.lineHeight = `${Math.round(fontSize * 1.3)}px`;
            }

            // AI message - same base for all, grows with height
            const aiMessage = aiSection.querySelector('.cash-flow-widget__ai-message') as HTMLElement;
            if (aiMessage) {
                const baseFontSize = 14;
                const fontSize = Math.round(baseFontSize * aiScaleFactor);
                const lineHeight = Math.round(fontSize * 1.4);
                aiMessage.style.fontSize = `${fontSize}px`;
                aiMessage.style.lineHeight = `${lineHeight}px`;
                aiMessage.style.height = 'auto';
                // Only limit height for medium, allow growth for half/full
                aiMessage.style.maxHeight = isMedium ? `${lineHeight * 2}px` : 'none';
            }

            // Chat button - same base for all, grows with height
            const chatButton = aiSection.querySelector('.cash-flow-widget__chat-button') as HTMLElement;
            if (chatButton) {
                const baseFontSize = 13;
                const fontSize = Math.round(baseFontSize * aiScaleFactor);
                const paddingV = Math.round(10 * aiScaleFactor);
                const paddingH = Math.round(14 * aiScaleFactor);
                chatButton.style.fontSize = `${fontSize}px`;
                chatButton.style.padding = `${paddingV}px ${paddingH}px`;
            }

            // AI content container - add gap
            const aiContent = aiSection.querySelector('.cash-flow-widget__ai-content') as HTMLElement;
            if (aiContent) {
                aiContent.style.gap = `${Math.round(3 * aiScaleFactor)}px`;
            }
        }

        // Update content gap
        const content = containerElement.querySelector('.cash-flow-widget__content') as HTMLElement;
        if (content) {
            const gap = Math.round((isMedium ? 6 : 10) * scaleFactor);
            content.style.gap = `${gap}px`;
        }
    }

    ngOnDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        this.subscriptions.unsubscribe();
    }
}
